import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import cn from 'classnames';

import Spinner from 'ui/Spinner';
import CertificationBadge from 'components/CertificationBadge';
import Confetti from 'components/ui/Confetti';
import DateFormat from 'constants/date_format';
import footerSrc from 'images/certification_footer.png';
import footerSrcPrint from 'images/certification_footer_no_background.png';
import logoSrc from 'images/seismic_learning_logo.png';
import useFeatureFlags from 'hooks/useFeatureFlags';
import Text from 'components/shared/Text';
import CertificationDust from './CertificationDust';

export default function CertificateView({
  isLoading,
  company,
  completedDate,
  expirationDate,
  expired,
  path,
  user,
  shouldShowConfetti,
  shouldPrint,
}) {
  const hasHidePoweredByLogoFF = useFeatureFlags([
    'hide_powered_by_lessonly_logo',
  ]);

  // Wait for the CertificationBadgeIcon to successfully load before printing
  const onLoadComplete = useCallback(() => {
    if (shouldPrint) {
      window.print();
    }
  }, [shouldPrint]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="CertificateView border border-gray-light pos-r ff-ss bg-gray-lightest">
      {shouldShowConfetti && !expired && <Confetti />}
      <div className="CertificateView-inner u-bgwhite d-f fd-c h-100">
        <div className="d-f ai-c w-fill">
          <span className="CertificateView-company-logo-wrapper border-right border-gray-light d-f ai-c jc-c f-1">
            <img
              alt="Company Logo"
              src={company.logoUrl}
              className="maxh-48 CertificateView-company-logo"
            />
          </span>
          <span className="CertificateView-modal-title fw-b f-3">
            {t('certifications.certificate.title')}
          </span>
        </div>

        <div className="CertificateView-middle d-f ai-fs w-fill">
          <div
            className={cn('f-1 d-f jc-c CertificateView-badge-section', {
              Expired: expired,
            })}
          >
            <div className="pos-r mt-24">
              <CertificationDust color={path.certification.backgroundColor} />
              <span className="CertificateView-badge">
                <CertificationBadge
                  onLoadComplete={onLoadComplete}
                  iconName={path.certification.iconName}
                  width={83}
                  height={105}
                  foregroundColor={path.certification.foregroundColor}
                  backgroundColor={path.certification.backgroundColor}
                  image={path.certification.image}
                />
              </span>
              <span className="CertificateView-badge--mobile">
                <CertificationBadge
                  iconName={path.certification.iconName}
                  width={32}
                  foregroundColor={path.certification.foregroundColor}
                  backgroundColor={path.certification.backgroundColor}
                  image={path.certification.image}
                />
              </span>
            </div>
          </div>
          <div className="CertificateView-acknowledgement d-f fd-c f-3">
            <span>{t('certifications.certificate.acknowledgement_start')}</span>
            <span className="w-fill wb-bw">
              <Text
                lines={2}
                className="CertificateView-username w-fill fw-b border-bottom border-gray-light"
              >
                {user.name}
              </Text>
            </span>
            <div className="CertificateView-line-top-margin">
              {t('certifications.certificate.acknowledgement_end')}
            </div>
            <span className="wb-bw wr-bw w-fill">
              <Text
                lines={4}
                className="CertificateView-title fw-b w-fill wb-bw wr-bw"
              >
                {path.title}
              </Text>
              <Text
                lines={2}
                className="CertificateView-title--mobile fw-b w-fill wb-bw wr-bw"
              >
                {path.title}
              </Text>
            </span>
            {moment(completedDate).isValid() && (
              <span className="CertificateView-line-top-margin">
                {t('certifications.certificate.certified_on')}
                {` ${moment(completedDate).format(DateFormat.LONG)}`}
              </span>
            )}
            {moment(expirationDate).isValid() && (
              <>
                {expired && (
                  <span className="c-error">
                    {t('certifications.certificate.expired_on')}
                    {` ${moment(expirationDate).format(DateFormat.LONG)}`}
                  </span>
                )}
                {!expired && (
                  <span className="c-midnight-60">
                    {t('certifications.certificate.expires_on')}
                    {` ${moment(expirationDate).format(DateFormat.LONG)}`}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-a">
          {!hasHidePoweredByLogoFF && (
            <>
              <span className="fs-i CertificateView-powered-by">
                {t('certifications.certificate.powered_by')}
              </span>
              <img
                alt="Lessonly Logo"
                className="ml-4 CertificateView-lessonly-logo"
                src={logoSrc}
              />
            </>
          )}
        </div>
      </div>
      <img
        alt="Certification Footer"
        src={footerSrc}
        className="pos-a CertificateView-footer-img"
      />
      <img
        alt="Certification Footer"
        src={footerSrcPrint}
        className="pos-a CertificateView-footer-img CertificateView-footer-img--print"
      />
    </div>
  );
}

CertificateView.propTypes = {
  company: PropTypes.shape({
    logoUrl: PropTypes.string,
  }),
  completedDate: PropTypes.string,
  path: PropTypes.shape({
    title: PropTypes.string,
    certification: PropTypes.shape({
      backgroundColor: PropTypes.string,
      foregroundColor: PropTypes.string,
      iconName: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({
    name: PropTypes.string,
  }),
};
